import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { ArrowRight, ShieldCheck, Zap, Sparkles, ShoppingBag, MapPin, Mail, Globe, Phone } from "lucide-react";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-background relative z-10">
      {/* Nav */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="landing-login-link"
              className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              data-testid="landing-getstarted-link"
              className="px-5 py-2.5 rounded-full text-sm font-semibold bg-primary text-white hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(211,23,43,0.3)] transition-all duration-150 inline-flex items-center gap-2"
            >
              Get credit <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16 overflow-hidden">
        <div className="blob bg-sunny w-72 h-72 -top-10 right-4 hidden sm:block" />
        <div className="blob bg-mint w-64 h-64 top-44 right-24 hidden sm:block" />
        <div className="blob bg-sky w-56 h-56 bottom-0 left-1/3 hidden lg:block" />
        <motion.div variants={stagger} initial="hidden" animate="show" className="relative z-10 max-w-3xl">
          <motion.p
            variants={item}
            className="text-xs font-medium tracking-overline text-primary uppercase mb-5"
          >
            For University & TVET students
          </motion.p>
          <motion.h1
            variants={item}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
          >
            Credit on the go.<br />
            <span className="text-primary">Pay as you use.</span>
          </motion.h1>
          <motion.p variants={item} className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl">
            Monthly student credit up to <span className="font-mono text-foreground font-semibold">R 1,000</span>. Repay just{" "}
            <span className="font-mono text-foreground font-semibold">R 1,289</span>. Spend on groceries and campus essentials,
            and earn points every time you use it.
          </motion.p>
          <motion.div variants={item} className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/register"
              data-testid="hero-getstarted-btn"
              className="px-6 py-3.5 rounded-full bg-primary text-white font-semibold inline-flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(211,23,43,0.3)] transition-all duration-150"
            >
              Earn points today <ArrowRight size={17} />
            </Link>
            <Link
              to="/login"
              className="px-6 py-3.5 rounded-full border-2 border-border font-semibold hover:border-primary hover:text-primary transition-colors duration-150"
            >
              I have an account
            </Link>
          </motion.div>
          <motion.p variants={item} className="mt-6 text-sm text-muted-foreground">
            Fees you can see before you borrow.
          </motion.p>
        </motion.div>
      </section>

      {/* Stat strip */}
      <section className="border-y border-border bg-card/40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {[
            ["R 1,000", "Monthly credit limit"],
            ["R 1,000", "Food & grocery credit"],
            ["R 1,289", "Simple flat repayment"],
            ["Pay-as-you-use", "Only pay for what you spend"],
          ].map(([big, small], i) => (
            <div key={i} className="p-6 sm:p-8">
              <p className="font-mono text-xl sm:text-2xl font-bold text-primary">{big}</p>
              <p className="mt-1 text-xs sm:text-sm text-muted-foreground">{small}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-10">One card. Everything campus.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            [Zap, "Instant credit", "Get approved and start spending your monthly credit right away."],
            [ShoppingBag, "Food & essentials", "Up to R1,000 grocery credit and a marketplace for student essentials."],
            [Sparkles, "Earn points", "Every purchase earns points you can track from your dashboard."],
            [ShieldCheck, "Transparent fees", "See exactly what you repay before you borrow. No surprises."],
          ].map(([Icon, title, desc], i) => (
            <div
              key={i}
              className="border-2 border-border rounded-2xl bg-card p-6 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(211,23,43,0.08)] transition-all duration-200"
              data-testid={`feature-card-${i}`}
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-2xl mb-4 ${["bg-primary/10 text-primary", "bg-mint/20 text-mint", "bg-sunny/30 text-[#141414]", "bg-sky/15 text-sky"][i]}`}>
                <Icon size={20} strokeWidth={2.5} />
              </div>
              <h3 className="font-bold mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
        <div className="rounded-3xl border-2 border-primary bg-primary/5 p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Get credit today.</h2>
            <p className="mt-2 text-muted-foreground">Create your student profile in minutes.</p>
          </div>
          <Link
            to="/register"
            data-testid="cta-register-btn"
            className="px-6 py-3.5 rounded-full bg-primary text-white font-semibold inline-flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(211,23,43,0.3)] transition-all duration-150 shrink-0"
          >
            Create profile <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col md:flex-row justify-between gap-6">
          <Logo />
          <div className="text-sm text-muted-foreground space-y-2">
            <p className="flex items-center gap-2"><Mail size={15} /> info@haki.capital</p>
            <p className="flex items-center gap-2"><Globe size={15} /> haki.capital</p>
            <p className="flex items-center gap-2"><Phone size={15} /> 070 515 0099</p>
            <p className="flex items-center gap-2"><MapPin size={15} /> 2 Bruton Road, Bryanston, 2191</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
